import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Constants, Positions, PositionValues } from 'app/core/Constants';
import { GenericSummaryCardDtoResponse } from 'app/core/models/generic-summary-card.dto-response';
import { PackageBuilderSummaryProduct } from 'app/core/models/package-builder-summary-product.model';
import { ProductCardDetailsDtoResponse } from 'app/core/models/product-card-details.dto-response';
import { ProductCardDtoResponse } from 'app/core/models/product-card-dto-response';
import { Product } from 'app/core/models/product.model';
import { User } from 'app/core/User';
import { PackageBuilderService } from 'app/package-builder/package-builder.service';
import { HardwareService } from 'app/shared/hardware.service';
import { NormalizeQtyPipe } from 'app/shared/pipe/normalize-qty.pipe';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-hardware-alternate-option-modal',
  templateUrl: './hardware-alternate-option-modal.component.html',
  styleUrls: ['./hardware-alternate-option-modal.component.css']
})
export class HardwareAlternateOptionModalComponent implements OnInit, OnDestroy {
  @Input() product: Product;
  @Input() minQuantity: number;
  @Input() defaultWarehouse: string;
  @Input() alternateProducts: GenericSummaryCardDtoResponse[];
  @Output() alternateProductSelected = new EventEmitter();
  @Output() continueBackOrder = new EventEmitter();
  alternateProductsList: ProductCardDtoResponse[];
  subs: Subscription[] = [];
  qty: number;
  packageBuilderService: PackageBuilderService
  constructor(public activeModal: NgbActiveModal, private injector: Injector,
    private hardwareSrv: HardwareService,
    private translateService: TranslateService,
    private normalizeQtyPipe: NormalizeQtyPipe) {
    this.packageBuilderService = this.injector.get<PackageBuilderService>(PackageBuilderService);
  }

  ngOnInit() {
    this.getAlternateProducts();
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }
  selectHardware(hardware: ProductCardDtoResponse) {
    //Add to packagebuilder and jump to next step ..
    //Add Product to PackageBuilder
    const position = this.product.position === PositionValues.REAR ? Positions.REAR : Positions.FRONT;
    this.packageBuilderService.updateHardware(this.createProduct(hardware), this.hardwareSummary(hardware), position);
    this.alternateProductSelected.next(hardware);
    this.activeModal.dismiss('AlternateOptionSelected');
  }
  closeModal(result?: boolean): void {
    this.continueBackOrder.next(result);
    if (result === true)
      this.activeModal.dismiss('Continue');
    else
      this.activeModal.dismiss('Cancel');
  }
  createProduct(hardware: ProductCardDtoResponse): Product {
    return new Product({
      id: hardware.longSKU,
      quantity: hardware.quantity,
      originalQty: hardware.quantity,
      warningNotes: this.product.warningNotes,
      type: Constants.PRODUCT_TYPE_HARDWARE,
      category: hardware.details.details.find(d => d.label === 'Type').accessoryType,
      pairNo: hardware.pairNo,
      position: this.product.position !== null ? this.product.position.toString() : null
    });
  }
  hardwareSummary(hardware: ProductCardDtoResponse): PackageBuilderSummaryProduct {
    const summaryProduct = new PackageBuilderSummaryProduct();
    summaryProduct.imgURL = hardware.productImage.imageUrl;
    summaryProduct.description = hardware.title;
    summaryProduct.shortSKU = hardware.shortSKU;
    summaryProduct.longSKU = hardware.longSKU;
    summaryProduct.qty = hardware.quantity;
    summaryProduct.retailPrice = parseFloat(hardware.retailPrice);
    summaryProduct.customerPrice = parseFloat(hardware.customerPrice);
    summaryProduct.landedCost = parseFloat(hardware.landedCost);
    summaryProduct.totalPrice = parseFloat(hardware.customerPrice) * hardware.quantity;
    summaryProduct.accessoryType = hardware.details.details.find(d => d.label === 'Type').accessoryType;
    summaryProduct.warningNotes = this.product.warningNotes;
    summaryProduct.pairNo = hardware.pairNo;
    if (this.product.position !== null)
      summaryProduct.position = this.product.position.toString();
    return summaryProduct;
  }

  getAlternateProducts() {
    this.alternateProductsList = new Array<ProductCardDtoResponse>();
    if (this.alternateProducts) {
      this.alternateProducts.forEach(c => {
        if (c.frontProduct) {
          c.frontProduct.quantity = this.minQuantity;
          this.alternateProductsList.push(c.frontProduct);
        }
      });
    }
    this.alternateProductsList.sort((a, b) => (parseFloat(a.matchingPercentage) > parseFloat(b.matchingPercentage) ? -1 : 1));
  }

  isMissingMSRP(hardware: ProductCardDtoResponse): boolean {
    let result = false;

    if (hardware.retailPrice === "9999.99")
      result = true;

    return result;
  }
  
  showCalgaryInventory(hardware: ProductCardDtoResponse): boolean {
    let show: boolean = false;
    if (hardware.inventory.onHandQtyCal != null) {
      show = true;
    }
    else if (hardware.inventory.inTransitQtyCal != null) {
      show = true;
    }
    return show;
  }
  getMTLInventoryTooltip(hardware: ProductCardDtoResponse): string {
    let toolTip: string = "";
    if (User.isFastcoRole && hardware.onHandDetails && hardware.onHandDetails.some(d => d.label.endsWith('ProcessRequired.MTL')))
      toolTip += `<u>${this.translateService.instant('FC.PackageBuilder.OutputProduct')}</u><br/><b>${hardware.shortSKU} - ${hardware.longSKU}</b><br/>`;
    toolTip += `<strong>${this.translateService.instant('FC.Inventory.AvailableOnHand')}: ${this.normalizeQtyPipe.transform(hardware.inventory.onHandQtyMtl, hardware.accessoryType != null ? "acc" : null)}</strong><br/>`;
    if (hardware.inventory.onOrderQtyMtl > 0 && hardware.inventory.onHandQtyMtl <= 0) {
      toolTip += "<strong>" + `${this.translateService.instant('FC.Inventory.BackOrdered')}` + "</strong><br/><br/>";
    }
    if (hardware.inventory.onOrderQtyMtl <= 0 && hardware.inventory.onHandQtyMtl == 0) {
      toolTip += "<strong>" + `${this.translateService.instant('FC.Inventory.OutOfStock')}` + "</strong><br/>";
    }
    if (hardware.onHandDetails) {
      hardware.onHandDetails.forEach(d => {
        if (d.label.endsWith('MTL')) {
          var qty = d.qty;
          if (hardware.onHandDetails.some(d => d.label.endsWith('MTL.hidden')))
            qty += hardware.onHandDetails.find(d => d.label.endsWith('MTL.hidden')).qty;
          toolTip += `${this.translateService.instant(d.label)}: ${this.normalizeQtyPipe.transform(qty, hardware.accessoryType != null ? "acc" : null)}<br/>`;
        }
      });
    }
    toolTip += `${this.translateService.instant('FC.PackageBuilder.OnOrder')}: ${this.normalizeQtyPipe.transform(hardware.inventory.onOrderQtyMtl, hardware.accessoryType != null ? "acc" : null)}<br/>`;
    if (hardware.inventory.onOrderETAMtl != null && hardware.inventory.onOrderETAMtl != "") {
      toolTip += `${hardware.inventory.onOrderETAMtl}`;
    }
    return toolTip;
  }

  getCALInventoryTooltip(hardware: ProductCardDtoResponse): string {
    let toolTip: string = "";
    toolTip += `<strong>${this.translateService.instant('FC.Inventory.AvailableOnHand')}: ${this.normalizeQtyPipe.transform(hardware.inventory.onHandQtyCal, hardware.accessoryType != null ? "acc" : null)}</strong><br/>`;
    if (hardware.inventory.inTransitQtyCal > 0 && hardware.inventory.onHandQtyCal <= 0) {
      toolTip += "<strong>" + `${this.translateService.instant('FC.Inventory.BackOrdered')}` + "</strong><br/><br/>";
    }
    if (hardware.inventory.inTransitQtyCal <= 0 && hardware.inventory.onHandQtyCal == 0) {
      toolTip += "<strong>" + `${this.translateService.instant('FC.Inventory.OutOfStock')}` + "</strong><br/ > ";
    }
    if (hardware.onHandDetails) {
      hardware.onHandDetails.forEach(d => {
        if (d.label.endsWith('CAL')) {
          var qty = d.qty;
          if (hardware.onHandDetails.some(d => d.label.endsWith('CAL.hidden')))
            qty += hardware.onHandDetails.find(d => d.label.endsWith('CAL.hidden')).qty;
          toolTip += `${this.translateService.instant(d.label)}: ${this.normalizeQtyPipe.transform(qty, hardware.accessoryType != null ? "acc" : null)}<br/>`;
        }
      });
    }
    toolTip += `${this.translateService.instant('FC.PackageBuilder.OnOrder')}: ${this.normalizeQtyPipe.transform(hardware.inventory.inTransitQtyCal, hardware.accessoryType != null ? "acc" : null)}`;
    if (hardware.inventory.inTransitETACal != null && hardware.inventory.inTransitETACal != "") {
      toolTip += `${hardware.inventory.inTransitETACal}`;
    }
    return toolTip;
  }
  getMTLInventoryBackorderStatus(hardware: ProductCardDtoResponse): string {
    let status: string = "";
    let onOrderQty: number = hardware.inventory.onOrderQtyMtl;
    let onHandQty: number = hardware.inventory.onHandQtyMtl;
    if (hardware.inventory.inTransitQtyPic !== null)
      onOrderQty += hardware.inventory.inTransitQtyPic;
    if (hardware.inventory.onHandQtyPic !== null)
      onHandQty += hardware.inventory.onHandQtyPic;

    if (onOrderQty > 0 && onHandQty <= 0) {
      status = "backorder"
    }
    if (onOrderQty == 0 && onHandQty <= 0) {
      status = "out"
    }
    return status;
  }

  getCALInventoryBackorderStatus(hardware: ProductCardDtoResponse): string {
    let status: string = "";
    if (hardware.inventory.inTransitQtyCal > 0 && hardware.inventory.onHandQtyCal <= 0) {
      status = "backorder"
    }
    if (hardware.inventory.inTransitQtyCal <= 0 && hardware.inventory.onHandQtyCal <= 0) {
      status = "out"
    }
    return status;
  }


  get showInventory(): boolean {
    var show = User.showInventory;
    return show;
  }
  get isFastcoUser(): boolean {
    return User.isFastcoRole;
  }
  
}
